import { Canceler } from 'axios';
import { action, computed, makeObservable, observable, toJS } from 'mobx';
import { MDF_AND_EXCL_MODAL_EXCLUSION, MDF_AND_EXCL_MODAL_MODIFIER, MDF_AND_EXCL_MODAL_SECTIONS } from '../constants';
import { defaultPagination, PER_PAGE } from 'src/constants/main';
import { modalStore } from 'src/mobx/modalStore';
import client from 'src/services/client';
import { IPagination, MODALS_CRUMB } from 'src/types/main';
import { IProductV4Exclusion, IProductV4Modifier } from 'src/types/product';
import { arrayMerge } from 'src/utils/formatter';
import { getCancelToken } from 'src/utils/helpers';
import logger from '~/services/logger';

class ModifiersAndExclusionsModalStore {
  constructor() {
    makeObservable(this);
  }

  cancelStack: {
    [key: string]: null | Canceler;
  } = {
    modifiers: null,
    exclusions: null,
    calculate: null,
  };

  @observable loading: boolean = false;

  @observable section: MDF_AND_EXCL_MODAL_SECTIONS = MDF_AND_EXCL_MODAL_SECTIONS.MODIFIERS;
  @observable activeCrumb: MODALS_CRUMB = MODALS_CRUMB.ALL;
  @observable searchValue: string = '';

  @observable selectedModifiers: IProductV4Modifier[] = [];
  @observable selectedExclusions: IProductV4Exclusion[] = [];

  @observable pagination: IPagination<IProductV4Modifier | IProductV4Exclusion> = defaultPagination;

  @computed get isMulti(): boolean {
    if (this.isSelectOnlyOneItemAvailable) return false;

    if (this.isModifiersSection) {
      return modalStore.modifierAndExclusionModal?.modifiersChoice === MDF_AND_EXCL_MODAL_MODIFIER.MULTIPLE;
    }

    return modalStore.modifierAndExclusionModal?.exclusionsChoice === MDF_AND_EXCL_MODAL_EXCLUSION.MULTIPLE;
  }

  @computed get isSelectOnlyOneItemAvailable(): boolean {
    return Boolean(modalStore.modifierAndExclusionModal?.selectOnlyOneItemAvailable);
  }

  @computed get disableModifierWithoutOptions(): boolean {
    return Boolean(modalStore.modifierAndExclusionModal?.disabledModifierWithoutOption);
  }

  @computed get isModifiersSection(): boolean {
    return this.section === MDF_AND_EXCL_MODAL_SECTIONS.MODIFIERS;
  }

  @computed get isSelectedCrumb(): boolean {
    return this.activeCrumb === MODALS_CRUMB.SELECTED;
  }

  @computed get isOnlyModifiers(): boolean {
    return modalStore.modifierAndExclusionModal?.exclusionsChoice === MDF_AND_EXCL_MODAL_EXCLUSION.NONE;
  }

  @computed get currentSectionSelectedItems(): IProductV4Modifier[] | IProductV4Exclusion[] {
    return this.isModifiersSection ? this.selectedModifiers : this.selectedExclusions;
  }

  public initStore = () => {
    this.section = modalStore.modifierAndExclusionModal?.defaultSection || MDF_AND_EXCL_MODAL_SECTIONS.MODIFIERS;
    this.activeCrumb = MODALS_CRUMB.ALL;
    this.searchValue = '';
    this.selectedModifiers = this.isSelectOnlyOneItemAvailable ? [] : toJS(modalStore.modifierAndExclusionModal?.selectedModifiers) || [];
    this.selectedExclusions = this.isSelectOnlyOneItemAvailable ? [] : toJS(modalStore.modifierAndExclusionModal?.selectedExclusions) || [];
    this.pagination = defaultPagination;

    this.fetchData(1);
  };

  @action fetchModifiers = async (page: number = this.pagination.current_page + 1) => {
    try {
      if (this.cancelStack.modifiers) this.cancelStack.modifiers('Cancelled');
      if (this.isSelectedCrumb) return;
      this.loading = true;

      const response: IPagination<IProductV4Modifier> = await client.get(`/v3/modifiers`, {
        params: { page, name: this.searchValue, is_delete: 0, status: 1 },
        headers: { 'Accept-Language': 'en,ar' },
        cancelToken: getCancelToken((c) => (this.cancelStack.modifiers = c)),
      });

      this.pagination = response;
      this.loading = false;
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.loading = false;
      logger.error({ error, place: 'ModifiersAndExclusionsModalStore fetchModifiers' });
    }
  };

  @action fetchExclusions = async (page: number = this.pagination.current_page + 1) => {
    try {
      if (this.cancelStack.exclusions) this.cancelStack.exclusions('Cancelled');
      if (this.isSelectedCrumb) return;
      this.loading = true;

      const response: IPagination<IProductV4Exclusion> = await client.get(`/v3/exclusions`, {
        params: { page, name: this.searchValue, is_delete: 0, status: 1 },
        headers: { 'Accept-Language': 'en,ar' },
        cancelToken: getCancelToken((c) => (this.cancelStack.exclusions = c)),
      });

      this.pagination = response;
      this.loading = false;
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.loading = false;
      logger.error({ error, place: 'ModifiersAndExclusionsModalStore fetchExclusions' });
    }
  };

  public fetchData = (page: number) => {
    if (this.isModifiersSection) {
      this.fetchModifiers(page);
    } else {
      this.fetchExclusions(page);
    }
  };

  @action changeSection = (section: MDF_AND_EXCL_MODAL_SECTIONS) => {
    this.section = section;
    this.activeCrumb = MODALS_CRUMB.ALL;
    this.searchValue = '';

    this.fetchData(1);
  };

  @action changeCrumb = (crumb: MODALS_CRUMB) => {
    this.activeCrumb = crumb;
    this.fetchData(1);
  };

  @action onSearch = (search: string) => {
    this.searchValue = search;
    this.fetchData(1);
  };

  @action selectItem = (item: IProductV4Modifier | IProductV4Exclusion) => {
    if (this.isSelectOnlyOneItemAvailable) {
      this.selectedModifiers = this.isModifiersSection ? this.selectHandler(item, toJS(this.selectedModifiers)) : [];
      this.selectedExclusions = this.isModifiersSection ? [] : this.selectHandler(item, toJS(this.selectedExclusions));

      return;
    }

    if (this.isModifiersSection) {
      this.selectedModifiers = this.selectHandler(item, toJS(this.selectedModifiers));
    } else {
      this.selectedExclusions = this.selectHandler(item, toJS(this.selectedExclusions));
    }
  };
  private selectHandler = (item: IProductV4Modifier | IProductV4Exclusion, array: any): any => {
    const alreadySelect: boolean = array.some((_item: any) => item.id === _item.id);

    if (alreadySelect) {
      return array.filter((_item: any) => item.id !== _item.id);
    }
    if (this.isMulti) {
      return array.concat(item);
    }

    return [item];
  };

  @action deselectAll = () => {
    if (this.isModifiersSection) {
      this.selectedModifiers = [];
    } else {
      this.selectedExclusions = [];
    }
  };

  @action selectAll = () => {
    if (this.isModifiersSection) {
      this.selectAllModifiers();
    } else {
      this.selectAllExclusions();
    }
  };
  private selectAllModifiers = async () => {
    try {
      if (this.cancelStack.modifiers) this.cancelStack.modifiers('Cancelled');
      this.loading = true;

      const response: IPagination<IProductV4Modifier> = await client.get(`/v3/modifiers`, {
        params: { page: 1, per_page: PER_PAGE.MAXIMUM, is_delete: 0, status: 1 },
        headers: { 'Accept-Language': 'en,ar' },
        cancelToken: getCancelToken((c) => (this.cancelStack.modifiers = c)),
      });

      const allAvailableModifiers: IProductV4Modifier[] = this.disableModifierWithoutOptions
        ? response.data.filter((modifier) => modifier.options.filter((option) => !option.is_delete).length)
        : response.data;

      this.selectedModifiers = [...arrayMerge(this.selectedModifiers, allAvailableModifiers, 'id')];

      this.loading = false;
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.loading = false;
      logger.error({ error, place: 'ModifiersAndExclusionsModalStore selectAllModifiers' });
    }
  };
  private selectAllExclusions = async () => {
    try {
      if (this.cancelStack.exclusions) this.cancelStack.exclusions('Cancelled');
      this.loading = true;

      const response: IPagination<IProductV4Exclusion> = await client.get(`/v3/exclusions`, {
        params: { page: 1, per_page: PER_PAGE.MAXIMUM, is_delete: 0, status: 1 },
        headers: { 'Accept-Language': 'en,ar' },
        cancelToken: getCancelToken((c) => (this.cancelStack.exclusions = c)),
      });

      this.selectedExclusions = [...arrayMerge(this.selectedExclusions, response.data, 'id')];
      this.loading = false;
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.loading = false;
      logger.error({ error, place: 'ModifiersAndExclusionsModalStore selectAllExclusions' });
    }
  };

  @action clickSave = () => {
    modalStore.modifierAndExclusionModal?.onSave(this.selectedModifiers, this.selectedExclusions);
    this.closeModal();
  };

  @action closeModal = () => {
    modalStore.closeModifierAndExclusionModal();
    this.clearStore();
  };
  private clearStore = () => {
    Object.values(this.cancelStack).forEach((token) => (token ? token('Cancelled') : null));
  };
}

export const mdfAndExclModalStore = new ModifiersAndExclusionsModalStore();
